import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {QlJoinDto} from 'features/core/shared/ql/data/dto/ql-join.dto';
import {Sorting} from 'features/core/shared/misc/sorting';
import {CpWidgetColumn} from './cp-widget-column';
import {ProvisionAssignmentConditionMode} from '../../enum/provision-assignment-condition-mode.enum';
import {CpAssignmentNameJoin} from '../../interface/cp-assignment-name-join.interface';

/**
 * Column and query curation shared by the SIX assignment tables of the ProvisionType object page
 * (K-05 Relevanz, K-06 Basiswert-Zuordnung, K-07 Haftungs-Zuordnung, K-10 Matrix-Zuordnung, K-17
 * Berechnete Vermittler, K-18 Komponenten-Eignung) and by the matrix-scoped K-10 list on the
 * navItem "Matrizen".
 *
 * ## Why this file exists (assignment-condition redesign)
 * The 13 contract dimensions left the six assignment entities; each assignment now references ONE
 * optional `cp.ProvisionAssignmentConditionSet`. The tables therefore no longer probe and splice
 * dimension columns — every one of them shows the same fixed block instead:
 *
 *   Name · Alias · Bedingungssatz · Priorität · <family columns> · Gültig ab · Gültig bis · Status · <result>
 *
 * The set is joined EXPLICITLY under the alias {@link CP_CONDITION_SET_JOIN_AS} so the column path
 * does not depend on the positional `fkeyt_e_<N>` index of `resolve_fkey_texts`, which would shift
 * with every relation the entity gains or loses (it just lost eleven).
 *
 * ## Why "Bedingungssatz" is a `#cust_` column
 * An EMPTY set is not a missing value but a statement: the assignment applies to every contract
 * ("Alle Verträge (unbedingt)"). A plain `pacs.name` column would render that as a blank cell that
 * reads exactly like "not maintained". The column is therefore a custom-field column
 * ({@link CP_CONDITION_SET_FIELD_ID}); the hosts render it through an
 * `<ng-template mvs-widget-custom-field-directive fieldId="conditionSet">`. It lives in the
 * descriptor's own `widgetForm` default layout (the TW-6 route), so it shows without a saved
 * layout; a user layout SAVED for the alias before this change replaces the default layout and must
 * be reset once to pick the column up (fe_core_widget rule 6).
 */

/** Join alias of `provisionAssignmentConditionSet` in every assignment query. */
export const CP_CONDITION_SET_JOIN_AS = 'pacs';

/** `fieldId` of the custom "Bedingungssatz" template; the layout field is `#cust_conditionSet`. */
export const CP_CONDITION_SET_FIELD_ID = 'conditionSet';

/** German text of an assignment WITHOUT condition set. */
export const CP_UNCONDITIONAL_LABEL = 'Alle Verträge (unbedingt)';

/** The "Bedingungssatz" column, ready for a {@link CpWidgetColumn} list. */
export const CP_CONDITION_SET_COLUMN: CpWidgetColumn = {
    field: '#cust_' + CP_CONDITION_SET_FIELD_ID,
    label: 'Bedingungssatz'
};

/** Leading block shared by every assignment table: Name, Alias, Bedingungssatz, Priorität. */
export const CP_ASSIGNMENT_LEADING_COLUMNS: CpWidgetColumn[] = [
    {field: 'e.name', label: 'Name'},
    {field: 'e.alias', label: 'Alias'},
    CP_CONDITION_SET_COLUMN,
    {field: 'e.orderPriority', label: 'Priorität'}
];

/** Validity and status block, placed before the assignment result. */
export const CP_ASSIGNMENT_PERIOD_COLUMNS: CpWidgetColumn[] = [
    {field: 'e.startDate', label: 'Gültig ab'},
    {field: 'e.endDate', label: 'Gültig bis'},
    {field: 'e.entityStatus', label: 'Status'}
];

/**
 * Build the explicit-join query of one assignment table WITHOUT filters — the child table merges
 * the parent FK filter and any pre-filter in.
 *
 * Always joins the condition set ({@link CP_CONDITION_SET_JOIN_AS}: `id`, `name`, `alias`); the
 * set id is what the row selection hands to the read-only condition preview.
 *
 * @param queryName   stable QL query name, `config.cp.<Entity>`
 * @param entity      the assignment entity, e.g. `cp.ProvisionTypeMatrixAssignment`
 * @param nameJoins   family-specific FK texts (component, result, …)
 * @param extraJoins  joins that do not fit the `name` shape (e.g. the two-hop agent joins of K-17)
 */
export function buildCpAssignmentQuery(queryName: string,
                                       entity: string,
                                       nameJoins: CpAssignmentNameJoin[] = [],
                                       extraJoins: QlJoinDto[] = []): QlQueryDto {
    return {
        name: queryName,
        type: 'view',
        start: {
            type: 'entity',
            name: entity,
            as: 'e',
            joins: [
                {
                    type: 'entity',
                    name: 'provisionAssignmentConditionSet',
                    as: CP_CONDITION_SET_JOIN_AS,
                    joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}, {name: 'alias'}]
                },
                ...nameJoins.map(join => ({
                    type: 'entity' as const,
                    name: join.relation,
                    as: join.as,
                    joinType: 'left' as const,
                    fields: [{name: 'name'}]
                })),
                ...extraJoins
            ]
        },
        // The engine evaluates the rows in exactly this order and takes the first match.
        sortings: [new Sorting('e.orderPriority', true), new Sorting('e.id', true)]
    };
}

/**
 * Read one value out of whatever a widget hands to a custom-field template or a selection event.
 *
 * The table passes `{row, dto}` to a `#cust_` template (`MvsWidgetTableComponent.handleRow`) and
 * the flat QL entry as `ObjectIdentifierData.data` on selection; QL keys are `<as>.<attribute>`.
 * Same lookup chain as `BdDunningStopWaitingTabComponent.rowValue`.
 */
export function cpRowValue(object: any, key: string): any {
    return object?.row?.dto?.[key]
        ?? object?.row?.[key]
        ?? object?.dto?.[key]
        ?? object?.data?.[key]
        ?? object?.[key]
        ?? null;
}

/**
 * The condition mode of a QL row as the enum — the QL pipe may deliver the ordinal or the constant
 * name. `null` for anything unreadable.
 */
export function cpConditionModeOf(value: any): ProvisionAssignmentConditionMode | null {
    if (typeof value === 'number') {
        return value === ProvisionAssignmentConditionMode.EXCLUDE
            ? ProvisionAssignmentConditionMode.EXCLUDE
            : ProvisionAssignmentConditionMode.INCLUDE;
    }
    if (typeof value === 'string' && value.trim().length) {
        const normalized = value.trim().toUpperCase();
        if (normalized === 'EXCLUDE' || normalized === String(ProvisionAssignmentConditionMode.EXCLUDE)) {
            return ProvisionAssignmentConditionMode.EXCLUDE;
        }
        return ProvisionAssignmentConditionMode.INCLUDE;
    }
    return null;
}

/** German label of a condition mode ("Einschließen" / "Ausschließen"). */
export function cpConditionModeLabel(mode: ProvisionAssignmentConditionMode | null): string {
    if (mode === ProvisionAssignmentConditionMode.EXCLUDE) {
        return 'Ausschließen';
    }
    if (mode === ProvisionAssignmentConditionMode.INCLUDE) {
        return 'Einschließen';
    }
    return '';
}

/** `p-tag` severity of a condition mode: INCLUDE green, EXCLUDE red. */
export function cpConditionModeSeverity(
    mode: ProvisionAssignmentConditionMode | null
): 'success' | 'danger' | 'secondary' {
    if (mode === ProvisionAssignmentConditionMode.EXCLUDE) {
        return 'danger';
    }
    if (mode === ProvisionAssignmentConditionMode.INCLUDE) {
        return 'success';
    }
    return 'secondary';
}

/** Font Awesome icon of a condition mode: plus for INCLUDE, minus for EXCLUDE. */
export function cpConditionModeIcon(mode: ProvisionAssignmentConditionMode | null): string {
    return mode === ProvisionAssignmentConditionMode.EXCLUDE ? 'fa-solid fa-minus' : 'fa-solid fa-plus';
}

/**
 * Dimension columns of the condition table (`CpProvisionAssignmentConditionService
 * .buildConditionsOfSetQuery` join aliases), keyed by the dimension's QL attribute — the
 * vocabulary `CpProvisionTypeDimensionService.getUsedDimensionAttributes` answers in. Headers are
 * the backend `@UiFieldInfo` labels of `cp.ProvisionAssignmentCondition`.
 */
export const CP_CONDITION_DIMENSION_COLUMNS: { [qlAttribute: string]: CpWidgetColumn } = {
    groupId: {field: 'g.name', label: 'Sparte'},
    productId: {field: 'prod.name', label: 'Produkt'},
    partnerId: {field: 'pa.name', label: 'Gesellschaft'},
    contractClosureChannelTypeId: {field: 'cct.name', label: 'Abschlussweg'},
    contractOriginTypeId: {field: 'cot.name', label: 'Herkunft'},
    contractBaseCategoryId: {field: 'cbc.name', label: 'Basiskategorie'},
    contractBaseTypeId: {field: 'cbt.name', label: 'Basisart'},
    categoryId: {field: 'cat.name', label: 'Kategorie'},
    contractTypeId: {field: 'ct.name', label: 'Vertragsart'},
    termMonthsFrom: {field: 'e.termMonthsFrom', label: 'Laufzeit von (Monate)'},
    termMonthsTo: {field: 'e.termMonthsTo', label: 'Laufzeit bis (Monate)'},
    formerContractRequirement: {field: 'e.formerContractRequirement', label: 'Vorvertrag'},
    formerContractPartnerId: {field: 'fpa.name', label: 'Gesellschaft Vorvertrag'},
    formerTermMonthsFrom: {field: 'e.formerTermMonthsFrom', label: 'Laufzeit Vorvertrag von (Monate)'},
    formerTermMonthsTo: {field: 'e.formerTermMonthsTo', label: 'Laufzeit Vorvertrag bis (Monate)'}
};
